import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { calculateLevelBasedPoints, POINTS } from '../utils/levelCalculation';
import type { Task } from '../types';
import type { SubTask } from '../types';
import { useActivityLogStore } from './activityLogStore';
import { useKanbanStore } from './kanbanStore';
import { getDescendants } from '../utils/taskTree';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';
import { cloudVersionsOf, createOwnRowSync, marksOf, mergeLoad, ownCache, rowFocusRefresh, type OwnRowSync, type RowMarks } from '../lib/ownRowSync';
import { refreshBlobOnFocus } from '../lib/blobFocusRefresh';
import { taskLivePoints, mayEarnAgain } from '../utils/taskPoints';

// Move a task (and its subtree) across categories / folders / parents / kanban
// columns. Field semantics: `undefined` = leave unchanged; `null` = clear
// (unassign folder / make top-level). `order` sets the dragged node's order.
export interface MoveTaskChanges {
  toCategory?: string;
  toFolderId?: string | null;
  toParentId?: string | null;
  toKanbanStatus?: string;
  order?: number;
}

// ─── Supabase sync helpers (taskStore pilot for the sync epic) ──────────────
// The `tasks` table keeps typed columns (used by the load filter + ordering)
// PLUS a `data` jsonb column holding the full Task. We write both; on read we
// reconstruct from `data` (falling back to the typed columns for legacy rows).
// All writes are fire-and-forget: optimistic local state first, cloud after.
// Failures only log — offline keeps working via the localStorage persist layer.
//
// ⚠️⚠️ A DELETED TASK MUST STAY DELETED (2026-10-18): writes were upserts and the load kept
// every cached task, so a stale tab re-inserted tasks deleted elsewhere — even with no user
// action (the load-time subtask migration wrote the whole merged set). Writes now go through
// `lib/ownRowSync.ts`: only tasks created here and unconfirmed (`unsyncedIds`) are inserted,
// every other write is an UPDATE, and a task the cloud no longer has is dropped here.
//
// ⚠️⚠️ THE READ POLICY SHOWS EVERY OWN TASK (migration 0303, 2026-10-19). Until then "Users
// can read filtered tasks" hid completed tasks last saved more than 30 days ago from their own
// owner (20 of 37 rows): an UPDATE couldn't see them, so writes to them never saved, and "0 rows"
// couldn't tell hidden from deleted. That 30-day filter is now the LOAD's (below), so an UPDATE
// that finds no row means the task was deleted elsewhere, exactly — and a cached task the load
// didn't return is a ghost only if the cloud has no row for it at all (`readAllTaskIds`).

const TASKS_TABLE = 'tasks';

// ─── The load: open tasks, and tasks saved in the last 30 days ──────────────
// A completed task last saved more than 30 days ago isn't loaded: it stays in the cloud, and a
// cached copy of it stays here. The filter was the read policy's until migration 0303, so the
// load returns the same rows (and costs the same) as before. Every write to a task bumps its
// `updated_at` (trigger `update_tasks_updated_at`), so an old task touched again loads again.
const LOAD_DAYS = 30;

/** UTC midnight 30 days back: the old policy's `CURRENT_DATE - 30 days` (the database runs in UTC). */
export function loadCutoff(now = Date.now()): string {
  const d = new Date(now);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - LOAD_DAYS)).toISOString();
}

const ID_PAGE = 1000; // PostgREST's `max_rows`; a lower cap only means more pages

/**
 * Every task id the cloud has for this user — what tells a task the load's filter left out
 * (old and completed: kept here) from one deleted elsewhere (dropped). Paged by id: each page
 * asks for the ids after the last one it got, and `count` (rows matching THAT request) says
 * whether the page held them all, so a row cap below `ID_PAGE` can't end the read early.
 */
async function readAllTaskIds(): Promise<{ ids: Set<string>; error: unknown }> {
  const ids = new Set<string>();
  let after: string | null = null;
  for (;;) {
    let page = supabase.from(TASKS_TABLE).select('id', { count: 'exact' }).order('id').limit(ID_PAGE);
    if (after) page = page.gt('id', after);
    const { data, error, count } = await page;
    if (error) return { ids, error };
    const rows = (data ?? []) as { id: string }[];
    rows.forEach((r) => ids.add(r.id));
    if (rows.length === 0 || rows.length >= (count ?? 0)) return { ids, error: null };
    after = rows[rows.length - 1].id;
  }
}

/** The row's columns, without its id (an UPDATE never sets the id). */
function taskFields(task: Task, userId: string) {
  return {
    user_id: userId,
    title: task.title,
    completed: task.completed ?? false,
    category: task.category ?? '',
    block_id: task.blockId ?? null,
    template_block_id: task.templateBlockId ?? null,
    sub_tasks: task.subTasks ?? [],
    point_record: task.pointRecord ?? null,
    order: task.order ?? 0,
    created_at: task.createdAt,
    updated_at: task.updatedAt,
    data: task,
  };
}

function rowToTask(row: any): Task {
  const data = row?.data;
  if (data && typeof data === 'object' && Object.keys(data).length > 0) {
    return data as Task;
  }
  // Legacy fallback: reconstruct from typed columns for rows written before
  // the `data` column existed.
  return {
    id: row.id,
    title: row.title,
    completed: row.completed ?? false,
    category: row.category ?? '',
    status: 'categorized',
    blockId: row.block_id ?? undefined,
    templateBlockId: row.template_block_id ?? undefined,
    subTasks: row.sub_tasks ?? [],
    pointRecord: row.point_record ?? undefined,
    order: row.order ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  } as Task;
}

// ── subTasks → task-tree migration (P4) ─────────────────────────────────────
// Convert each task's legacy subTasks[] into real child Tasks (parentTaskId =
// the parent, reusing the subtask id so it's idempotent — re-running finds the
// child already present and the parent's subTasks already cleared). Children
// inherit the parent's category + folder. Returns the rebuilt map plus the list
// of created/changed tasks to write through to the cloud — `created` names the
// new child tasks among them (rows the cloud has never had: they are INSERTED).
export function migrateSubtasksToTree(
  tasksByCat: Record<string, Task[]>,
): { tasks: Record<string, Task[]>; changed: Task[]; created: Task[] } {
  const existingIds = new Set<string>();
  Object.values(tasksByCat).flat().forEach((t) => existingIds.add(t.id));

  const changed: Task[] = [];
  const created: Task[] = [];
  const result: Record<string, Task[]> = {};
  const now = new Date().toISOString();

  for (const [cat, list] of Object.entries(tasksByCat)) {
    const out: Task[] = [];
    for (const task of list) {
      if (task.subTasks && task.subTasks.length > 0) {
        task.subTasks.forEach((st, i) => {
          if (existingIds.has(st.id)) return; // already migrated
          const child: Task = {
            id: st.id,
            title: st.title,
            completed: st.completed,
            category: task.category,
            status: 'categorized',
            folderId: task.folderId,
            parentTaskId: task.id,
            order: i,
            pointRecord: st.pointRecord,
            createdAt: task.createdAt ?? now,
            updatedAt: now,
          };
          out.push(child);
          changed.push(child);
          created.push(child);
          existingIds.add(st.id);
        });
        const cleared: Task = { ...task, subTasks: [], updatedAt: now };
        out.push(cleared);
        changed.push(cleared);
      } else {
        out.push(task);
      }
    }
    result[cat] = out;
  }
  return { tasks: result, changed, created };
}

const allTasks = (tasks: Record<string, Task[]>): Task[] => Object.values(tasks).flat();

const taskSync: OwnRowSync<Task> = createOwnRowSync<Task>({
  table: TASKS_TABLE,
  label: 'tasks',
  fields: taskFields,
  current: (id) => useTaskStore.getState().getTaskById(id),
  fromRow: rowToTask,
  // In its area's list — another device may have moved it to another area.
  replace: (task) => useTaskStore.setState((s) => {
    const from = Object.keys(s.tasks).find((cat) => s.tasks[cat].some((t) => t.id === task.id))!;
    const to = task.category ?? '';
    if (from === to) return { tasks: { ...s.tasks, [to]: s.tasks[to].map((t) => (t.id === task.id ? task : t)) } };
    return { tasks: { ...s.tasks, [from]: s.tasks[from].filter((t) => t.id !== task.id), [to]: [...(s.tasks[to] ?? []), task] } };
  }),
  unsyncedIds: () => useTaskStore.getState().unsyncedIds,
  confirm: (id) => useTaskStore.setState((s) =>
    s.unsyncedIds.includes(id) ? { unsyncedIds: s.unsyncedIds.filter((x) => x !== id) } : s),
  drop: (id) => useTaskStore.setState((s) => ({
    tasks: Object.fromEntries(Object.entries(s.tasks).map(([cat, list]) => [cat, list.filter((t) => t.id !== id)])),
    unsyncedIds: s.unsyncedIds.filter((x) => x !== id),
  })),
  marks: () => useTaskStore.getState(),
  setMarks: (marks) => useTaskStore.setState(marks),
  name: (t) => t.title,
});

/** Save these tasks (each through its own queue — see `lib/ownRowSync.ts`). */
const cloudSave = (tasks: Task | Task[]): Promise<boolean> => taskSync.save(tasks);

async function cloudDelete(ids: string | string[]): Promise<void> {
  const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
  if (list.length === 0) return;
  // ⚠️ After any save of these tasks already on the wire: a delete that overtook the task's
  // own insert would be undone by it.
  await taskSync.settle(list);
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return;
    // RLS scopes the delete to the current user's own rows.
    const { error } = await supabase.from(TASKS_TABLE).delete().in('id', list);
    if (error) console.error('Failed to delete task(s) from Supabase:', error);
  } catch (error) {
    console.error('Error deleting task(s) from Supabase:', error);
  }
}

interface TaskState extends RowMarks<Task> {
  tasks: Record<string, Task[]>;
  userLevel: number;
  // Which user the loaded `tasks` were hydrated for (transient — re-runs load
  // on account switch). null = not loaded this session.
  initializedUserId: string | null;
  // Which user the persisted local cache belongs to. Lets us tell our own
  // offline cache apart from another account's leftovers on a shared browser.
  cachedUserId: string | null;
  /**
   * Tasks created on THIS device whose insert the cloud hasn't confirmed yet (offline, or
   * still on the wire). The only tasks ever inserted, and the only cached tasks a load keeps
   * when the cloud has no row for them. Persisted.
   */
  unsyncedIds: string[];
  // `dirtyIds` + `cloudVersions` + `dirtyBases` (RowMarks, persisted): what a load decides by, without a clock — lib/ownRowSync.ts.
  initialize: () => Promise<void>;
  addTask: (task: Task) => void;
  toggleTask: (taskId: string, category: string | undefined) => void;
  deleteTask: (taskId: string, category: string | undefined) => void;
  updateTask: (taskId: string, category: string | undefined, updates: Partial<Task>) => void;
  moveTaskToBlock: (taskId: string, category: string | undefined, blockId: string, templateBlockId: string, syncId: string) => void;
  addSubTask: (taskId: string, subTask: SubTask, category: string | undefined) => void;
  toggleSubTask: (taskId: string, subTaskId: string, category: string | undefined) => void;
  deleteSubTask: (taskId: string, subTaskId: string, category: string | undefined) => void;
  getTasksByCategory: (category: string) => Task[];
  getTaskById: (taskId: string) => Task | undefined;
  unassignBlockTasks: (blockId: string) => void;
  setUserLevel: (level: number) => void;
  /**
   * Start a folder's next round: every task back to not done.
   * `payAgain` (the folder's "Allow XP on replay") — true: the XP each task
   * earned moves to `retainedPoints` and its record is cleared, so the next
   * completion pays again; false: the record stays, so it does not.
   * ⚠️ Either way no earned XP is lost (owner, 2026-09-17).
   */
  resetFolderTasks: (folderId: string, payAgain: boolean, shuffleOrder: boolean) => void;
  getTasksByFolder: (folderId: string) => Task[];
  reorderFolderTasks: (folderId: string, sourceIndex: number, destinationIndex: number) => void;
  assignTaskOrders: (folderId: string) => void;
  moveTask: (taskId: string, fromCategory: string | undefined, changes: MoveTaskChanges) => void;
}

type PersistedTaskState = Pick<TaskState, 'tasks' | 'userLevel' | 'cachedUserId' | 'unsyncedIds' | 'dirtyIds' | 'cloudVersions'>;

export const useTaskStore = create<TaskState>()(
  persist<TaskState, [], [], PersistedTaskState>(
    (set, get) => ({
      tasks: {},
      userLevel: 1,
      initializedUserId: null,
      cachedUserId: null,
      unsyncedIds: [],
      dirtyIds: [],
      cloudVersions: {},
      dirtyBases: {},

      // Load-on-login, keyed by user id. Re-runs when a DIFFERENT account signs
      // in (so account switches don't get stuck showing the previous user's
      // data). When the local cache belongs to the SAME user, the cloud's copy
      // wins except an unsaved edit of the task — merged onto the cloud's copy if
      // another device has written the task since — no clock decides
      // (`lib/ownRowSync.ts` mergeLoad); a cached task the cloud has
      // no row for was deleted elsewhere and goes, unless it is unsynced. A cached
      // task the load's filter left out (old and completed) is still in the cloud:
      // kept as cached. When the cache belongs to a DIFFERENT user, cloud is
      // authoritative so one account's tasks never bleed into another's view.
      // Logged out → no-op. What the merge kept over the cloud (unsynced, or an
      // unsaved edit) is then saved (`writeBack`).
      initialize: () => dedupeInit('tasks', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return; // already loaded for this user
          // Cache-first boot: while this user's local cache is fresh, skip the
          // cloud read entirely (write-through keeps cloud current meanwhile).
          if (get().cachedUserId === user.id && isCloudFresh('tasks', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }

          const cutoff = loadCutoff();
          const { result: [{ data, error }, all], win } = await taskSync.load(() => Promise.all([
            supabase.from(TASKS_TABLE).select('*').or(`completed.eq.false,updated_at.gte.${cutoff}`),
            readAllTaskIds(),
          ]));
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;
          if (all.error) throw all.error;

          const cloudTasks = (data ?? []).map(rowToTask);
          const versions = cloudVersionsOf(data ?? []);

          let migratedChanged: Task[] = [];
          let kept: string[] = [];
          set((state) => {
            const own = ownCache(state.cachedUserId, user.id, allTasks(state.tasks), state.unsyncedIds);
            const merged = mergeLoad({
              ...own,
              marks: marksOf(state),
              cloud: cloudTasks,
              versions,
              win,
              // In the cloud but left out by the load's filter: an old completed task, not a ghost.
              keep: (t) => all.ids.has(t.id),
              label: 'tasks',
            });
            kept = merged.kept;

            const byCategory: Record<string, Task[]> = {};
            merged.rows.forEach((task) => {
              const category = task.category ?? '';
              (byCategory[category] ||= []).push(task);
            });

            // Convert any legacy subTasks into real child tasks (idempotent).
            // ⚠️ An automatic writer: it runs on the merged set, ghosts already gone, and its
            // new child tasks are rows the cloud has never had — unsynced, so INSERTED.
            const { tasks: migrated, changed, created } = migrateSubtasksToTree(byCategory);
            migratedChanged = changed;

            return {
              tasks: migrated,
              unsyncedIds: [...merged.unsyncedIds, ...created.map((t) => t.id)],
              ...merged.marks,
              initializedUserId: user.id,
              cachedUserId: user.id,
            };
          });
          taskSync.seen(cloudTasks, win); // the base of each task's next edit (lib/ownRowSync.ts)
          if (migratedChanged.length) void cloudSave(migratedChanged);
          // What the merge kept over the cloud (a task made, or an edit, whose write never landed).
          void taskSync.writeBack(kept);
          markCloudFresh('tasks', user.id);
        } catch (error) {
          console.error('Failed to initialize tasks from Supabase:', error);
        }
      }),

      setUserLevel: (level) => set({ userLevel: level }),

      addTask: (task) => {
        set((state) => {
          const category = task.category ?? '';
          const categoryTasks = state.tasks[category ?? ''] || [];

          // Check if task already exists
          const existingTask = categoryTasks.find(t => t.id === task.id);
          if (existingTask) {
            return state;
          }

          // Assign order if task is in a folder and doesn't have an order
          let order = task.order;
          if (task.folderId && order === undefined) {
            const folderTasks = categoryTasks.filter(t => t.folderId === task.folderId);
            order = folderTasks.length;
          }

          // Ensure task has required fields
          const newTask: Task = {
            ...task,
            order,
            status: task.status || 'categorized',
            createdAt: task.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          return {
            tasks: {
              ...state.tasks,
              [category ?? '']: [...categoryTasks, newTask],
            },
            unsyncedIds: [...state.unsyncedIds, newTask.id], // ← INSERTED until the cloud confirms it
          };
        });

        const created = get().getTaskById(task.id);
        if (created) void cloudSave(created);
      },

      toggleTask: (taskId, category) => {
        const task = get().tasks[category ?? '']?.find(t => t.id === taskId);
        if (!task) return;

        const isCompleting = !task.completed;
        // ⚠️ Only a completion that MINTED is journaled — re-ticking a task that
        // already holds its record paid nothing, and the XP history sums the log.
        let minted = false;

        set((state) => {
          const categoryTasks = state.tasks[category ?? ''] || [];
          return {
            tasks: {
              ...state.tasks,
              [category ?? '']: categoryTasks.map((t) => {
                if (t.id !== taskId) return t;

                const completed = !t.completed;
                const now = new Date().toISOString();

                // Only create a new point record if the task is being completed and doesn't already have one
                // (and, after a replay, not twice in one day).
                let pointRecord = t.pointRecord;
                if (completed && !pointRecord && mayEarnAgain(t, new Date(now))) {
                  minted = true;
                  const basePoints = calculateLevelBasedPoints(POINTS.TASK, state.userLevel);
                  pointRecord = {
                    points: basePoints,
                    userLevel: state.userLevel,
                    earnedAt: now
                  };
                }

                // Update kanbanStatus if completed status changes
                let kanbanStatus = t.kanbanStatus;
                if (completed && (!kanbanStatus || kanbanStatus !== 'completed')) {
                  kanbanStatus = 'completed';
                } else if (!completed && kanbanStatus === 'completed') {
                  kanbanStatus = 'backlog';
                }

                return {
                  ...t,
                  completed,
                  kanbanStatus,
                  pointRecord,
                  updatedAt: now
                };
              }),
            },
          };
        });

        const toggled = get().tasks[category ?? '']?.find(t => t.id === taskId);
        if (toggled) void cloudSave(toggled);

        // Log activity if completing
        if (isCompleting && minted) {
          const updatedTask = get().tasks[category ?? '']?.find(t => t.id === taskId);
          if (updatedTask && updatedTask.pointRecord) {
            useActivityLogStore.getState().addActivity({
              activityType: 'task',
              activityName: updatedTask.title,
              domainId: updatedTask.category || category || '',
              xpEarned: updatedTask.pointRecord.points,
              metadata: {
                taskId: updatedTask.id,
                category: category
              },
              completedAt: new Date().toISOString()
            });
          }
        }
      },

      deleteTask: (taskId, category) => {
        taskSync.noteDeleted(taskId);
        set((state) => {
          const categoryTasks = state.tasks[category ?? ''] || [];
          return {
            tasks: {
              ...state.tasks,
              [category ?? '']: categoryTasks.filter((task) => task.id !== taskId),
            },
            unsyncedIds: state.unsyncedIds.filter((id) => id !== taskId),
          };
        });

        void cloudDelete(taskId);
      },

      updateTask: (taskId, category, updates) => {
        set((state) => {
          const categoryTasks = state.tasks[category ?? ''] || [];
          const task = categoryTasks.find(t => t.id === taskId);
          if (!task) return state;

          // If category is being changed, move task to new category
          if (updates.category && updates.category !== category) {
            const newCategoryTasks = state.tasks[updates.category] || [];
            const updatedTask = { 
              ...task, 
              ...updates,
              updatedAt: new Date().toISOString()
            };

            return {
              tasks: {
                ...state.tasks,
                [category ?? '']: categoryTasks.filter(t => t.id !== taskId),
                [updates.category]: [...newCategoryTasks, updatedTask]
              }
            };
          }

          // If kanbanStatus is being updated to 'completed', also update completed status
          if (updates.kanbanStatus === 'completed' && !task.completed) {
            updates.completed = true;
            
            // Only create a new point record if one doesn't already exist
            // (and, after a replay, not twice in one day).
            if (!task.pointRecord && mayEarnAgain(task)) {
              // Calculate points if being completed
              const basePoints = calculateLevelBasedPoints(POINTS.TASK, state.userLevel);
              updates.pointRecord = {
                points: basePoints,
                userLevel: state.userLevel,
                earnedAt: new Date().toISOString()
              };
            }
          } 
          // If kanbanStatus is being updated from 'completed' to something else, update completed status
          else if (updates.kanbanStatus && updates.kanbanStatus !== 'completed' && task.completed) {
            updates.completed = false;
          }

          // Regular update
          return {
            tasks: {
              ...state.tasks,
              [category ?? '']: categoryTasks.map(t =>
                t.id === taskId ? {
                  ...t,
                  ...updates,
                  updatedAt: new Date().toISOString()
                } : t
              )
            }
          };
        });

        const updated = get().getTaskById(taskId);
        if (updated) void cloudSave(updated);
      },

      moveTaskToBlock: (taskId, category, blockId, templateBlockId, syncId) => {
        set((state) => {
          const categoryTasks = state.tasks[category ?? ''] || [];
          const task = categoryTasks.find(t => t.id === taskId);
          if (!task) return state;

          // Update task with block associations
          const updatedTask = {
            ...task,
            blockId,
            templateBlockId,
            syncId,
            status: 'scheduled' as const,
            updatedAt: new Date().toISOString()
          };

          return {
            tasks: {
              ...state.tasks,
              [category ?? '']: categoryTasks.map(t =>
                t.id === taskId ? updatedTask : t
              )
            }
          };
        });

        const moved = get().getTaskById(taskId);
        if (moved) void cloudSave(moved);
      },

      addSubTask: (taskId, subTask, category) => {
        set((state) => {
          const categoryTasks = state.tasks[category ?? ''] || [];
          return {
            tasks: {
              ...state.tasks,
              [category ?? '']: categoryTasks.map((task) =>
                task.id === taskId
                  ? {
                      ...task,
                      subTasks: [...(task.subTasks || []), subTask],
                      updatedAt: new Date().toISOString()
                    }
                  : task
              ),
            },
          };
        });

        const withSub = get().getTaskById(taskId);
        if (withSub) void cloudSave(withSub);
      },

      toggleSubTask: (taskId, subTaskId, category) => {
        const task = get().tasks[category ?? '']?.find(t => t.id === taskId);
        if (!task || !task.subTasks) return;

        const subTask = task.subTasks.find(st => st.id === subTaskId);
        if (!subTask) return;

        const isCompleting = !subTask.completed;

        set((state) => {
          const categoryTasks = state.tasks[category ?? ''] || [];
          return {
            tasks: {
              ...state.tasks,
              [category ?? '']: categoryTasks.map((t) => {
                if (t.id !== taskId || !t.subTasks) return t;

                const now = new Date().toISOString();

                return {
                  ...t,
                  subTasks: t.subTasks.map((st) => {
                    if (st.id !== subTaskId) return st;

                    const completed = !st.completed;

                    // Only create a new point record if the subtask is being completed and doesn't already have one
                    let pointRecord = st.pointRecord;
                    if (completed && !pointRecord) {
                      const basePoints = calculateLevelBasedPoints(POINTS.SUBTASK, state.userLevel);
                      pointRecord = {
                        points: basePoints,
                        userLevel: state.userLevel,
                        earnedAt: now
                      };
                    }

                    return {
                      ...st,
                      completed,
                      pointRecord
                    };
                  }),
                  updatedAt: now
                };
              }),
            },
          };
        });

        const subToggled = get().tasks[category ?? '']?.find(t => t.id === taskId);
        if (subToggled) void cloudSave(subToggled);

        // Log activity if completing
        if (isCompleting) {
          const updatedTask = get().tasks[category ?? '']?.find(t => t.id === taskId);
          const updatedSubTask = updatedTask?.subTasks?.find(st => st.id === subTaskId);
          if (updatedTask && updatedSubTask && updatedSubTask.pointRecord) {
            useActivityLogStore.getState().addActivity({
              activityType: 'task',
              activityName: `${updatedTask.title} - ${updatedSubTask.title}`,
              domainId: updatedTask.category || category || '',
              xpEarned: updatedSubTask.pointRecord.points,
              metadata: {
                taskId: updatedTask.id,
                subTaskId: updatedSubTask.id,
                category: category,
                isSubTask: true
              },
              completedAt: new Date().toISOString()
            });
          }
        }
      },

      deleteSubTask: (taskId, subTaskId, category) => {
        set((state) => {
          const categoryTasks = state.tasks[category ?? ''] || [];
          return {
            tasks: {
              ...state.tasks,
              [category ?? '']: categoryTasks.map((task) =>
                task.id === taskId && task.subTasks
                  ? {
                      ...task,
                      subTasks: task.subTasks.filter((st) => st.id !== subTaskId),
                      updatedAt: new Date().toISOString()
                    }
                  : task
              ),
            },
          };
        });

        const afterSubDelete = get().getTaskById(taskId);
        if (afterSubDelete) void cloudSave(afterSubDelete);
      },

      getTasksByCategory: (category) => {
        const tasks = get().tasks[category ?? ''] || [];
        // Return all tasks, including those with:
        // 1. Active block assignment (blockId)
        // 2. Template block association (templateBlockId)
        // 3. Permanent block association (syncId)
        // 4. Unassigned tasks (none of the above)
        return tasks;
      },

      getTaskById: (taskId) => {
        const allTasks = Object.values(get().tasks).flat();
        return allTasks.find(task => task.id === taskId);
      },

      unassignBlockTasks: (blockId) => {
        const affectedIds = Object.values(get().tasks)
          .flat()
          .filter((t) => t.blockId === blockId)
          .map((t) => t.id);

        set((state) => {
          const updatedTasks = { ...state.tasks };

          Object.keys(updatedTasks).forEach(category => {
            updatedTasks[category] = updatedTasks[category].map(task => {
              if (task.blockId === blockId) {
                const { blockId: _, ...taskWithoutBlockId } = task;
                return {
                  ...taskWithoutBlockId,
                  updatedAt: new Date().toISOString()
                };
              }
              return task;
            });
          });

          return { tasks: updatedTasks };
        });

        const updated = affectedIds
          .map((id) => get().getTaskById(id))
          .filter(Boolean) as Task[];
        void cloudSave(updated);
      },

      resetFolderTasks: (folderId, payAgain, shuffleOrder) => {
        const affectedIds = get().getTasksByFolder(folderId).map((t) => t.id);

        set((state) => {
          const updatedTasks = { ...state.tasks };
          const now = new Date().toISOString();

          const tasksToReset: Task[] = [];

          Object.keys(updatedTasks).forEach(category => {
            updatedTasks[category] = updatedTasks[category].map(task => {
              if (task.folderId === folderId) {
                tasksToReset.push(task);
                // ⚠️⚠️ Clearing a record without banking it ERASED the XP: every
                // total sums the records (this was live until 2026-09-17).
                const banked = payAgain ? taskLivePoints(task) : 0;
                // Remember when that XP was earned: the next round may pay only on a later day.
                const earnedAt = banked ? task.pointRecord?.earnedAt : undefined;
                const lastEarnedAt = earnedAt && (!task.lastEarnedAt || earnedAt > task.lastEarnedAt)
                  ? earnedAt : task.lastEarnedAt;
                return {
                  ...task,
                  completed: false,
                  kanbanStatus: 'backlog',
                  subTasks: task.subTasks?.map(st => ({
                    ...st,
                    completed: false,
                    pointRecord: payAgain ? undefined : st.pointRecord
                  })),
                  pointRecord: payAgain ? undefined : task.pointRecord,
                  ...(banked ? { retainedPoints: (task.retainedPoints ?? 0) + banked, lastEarnedAt } : {}),
                  updatedAt: now
                };
              }
              return task;
            });

            if (shuffleOrder) {
              const folderTasks = updatedTasks[category].filter(t => t.folderId === folderId);
              const nonFolderTasks = updatedTasks[category].filter(t => t.folderId !== folderId);

              for (let i = folderTasks.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [folderTasks[i], folderTasks[j]] = [folderTasks[j], folderTasks[i]];
              }

              updatedTasks[category] = [...nonFolderTasks, ...folderTasks];
            }
          });

          return { tasks: updatedTasks };
        });

        const updated = affectedIds
          .map((id) => get().getTaskById(id))
          .filter(Boolean) as Task[];
        void cloudSave(updated);
      },

      getTasksByFolder: (folderId) => {
        const allTasks = Object.values(get().tasks).flat();
        return allTasks.filter(task => task.folderId === folderId);
      },

      reorderFolderTasks: (folderId, sourceIndex, destinationIndex) => {
        const affectedIds = get().getTasksByFolder(folderId).map((t) => t.id);

        set((state) => {
          const updatedTasks = { ...state.tasks };
          const now = new Date().toISOString();

          Object.keys(updatedTasks).forEach(category => {
            const folderTasks = updatedTasks[category]
              .filter(t => t.folderId === folderId)
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

            const nonFolderTasks = updatedTasks[category].filter(t => t.folderId !== folderId);

            if (folderTasks.length > 0 && sourceIndex < folderTasks.length && destinationIndex < folderTasks.length) {
              const [movedTask] = folderTasks.splice(sourceIndex, 1);
              folderTasks.splice(destinationIndex, 0, movedTask);

              const reorderedTasks = folderTasks.map((task, index) => ({
                ...task,
                order: index,
                updatedAt: now
              }));

              updatedTasks[category] = [...nonFolderTasks, ...reorderedTasks];
            }
          });

          return { tasks: updatedTasks };
        });

        const updated = affectedIds
          .map((id) => get().getTaskById(id))
          .filter(Boolean) as Task[];
        void cloudSave(updated);
      },

      assignTaskOrders: (folderId) => {
        const affectedIds = get().getTasksByFolder(folderId).map((t) => t.id);

        set((state) => {
          const updatedTasks = { ...state.tasks };
          const now = new Date().toISOString();

          Object.keys(updatedTasks).forEach(category => {
            const folderTasks = updatedTasks[category]
              .filter(t => t.folderId === folderId);

            const nonFolderTasks = updatedTasks[category].filter(t => t.folderId !== folderId);

            const orderedTasks = folderTasks.map((task, index) => ({
              ...task,
              order: task.order !== undefined ? task.order : index,
              updatedAt: now
            }));

            updatedTasks[category] = [...nonFolderTasks, ...orderedTasks];
          });

          return { tasks: updatedTasks };
        });

        const updated = affectedIds
          .map((id) => get().getTaskById(id))
          .filter(Boolean) as Task[];
        void cloudSave(updated);
      },

      moveTask: (taskId, fromCategory, changes) => {
        const fromCat = fromCategory ?? '';
        const sourceBucket = get().tasks[fromCat] || [];
        const node = sourceBucket.find((t) => t.id === taskId);
        if (!node) return;

        const targetCat = changes.toCategory ?? fromCat;
        const categoryChanged = targetCat !== fromCat;

        // The dragged node plus its whole subtree (children share the parent's
        // category/folder, so they travel together).
        const descendants = getDescendants(sourceBucket, taskId);
        const movingIds = new Set<string>([taskId, ...descendants.map((d) => d.id)]);

        const newFolderId =
          changes.toFolderId === null ? undefined
          : changes.toFolderId !== undefined ? changes.toFolderId
          : node.folderId;
        const newParentId =
          changes.toParentId === null ? undefined
          : changes.toParentId !== undefined ? changes.toParentId
          : node.parentTaskId;

        let kanbanStatus = node.kanbanStatus;
        let completedOverride: boolean | undefined;
        if (changes.toKanbanStatus !== undefined) {
          kanbanStatus = changes.toKanbanStatus;
          const col = useKanbanStore.getState().getColumn(changes.toKanbanStatus);
          if (col?.isDone && !node.completed) completedOverride = true;
          else if (col && !col.isDone && node.completed) completedOverride = false;
        }

        const now = new Date().toISOString();

        set((state) => {
          const src = (state.tasks[fromCat] || []).filter((t) => !movingIds.has(t.id));

          const movedNodes: Task[] = [node, ...descendants].map((t) => {
            if (t.id === taskId) {
              return {
                ...t,
                category: targetCat,
                folderId: newFolderId,
                parentTaskId: newParentId,
                kanbanStatus,
                ...(completedOverride !== undefined ? { completed: completedOverride } : {}),
                ...(changes.order !== undefined ? { order: changes.order } : {}),
                updatedAt: now,
              };
            }
            // Descendant inherits the subtree's category + folder.
            return { ...t, category: targetCat, folderId: newFolderId, updatedAt: now };
          });

          if (categoryChanged) {
            const targetExisting = state.tasks[targetCat] || [];
            return {
              tasks: {
                ...state.tasks,
                [fromCat]: src,
                [targetCat]: [...targetExisting, ...movedNodes],
              },
            };
          }
          return {
            tasks: { ...state.tasks, [fromCat]: [...src, ...movedNodes] },
          };
        });

        const updated = [...movingIds]
          .map((id) => get().getTaskById(id))
          .filter(Boolean) as Task[];
        void cloudSave(updated);
      },
    }),
    {
      name: 'tasks-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 9,
      // Persist the cache + who it belongs to. `initializedUserId` stays
      // transient so load-on-login runs on every fresh app start.
      partialize: (state) => ({
        tasks: state.tasks,
        userLevel: state.userLevel,
        cachedUserId: state.cachedUserId,
        // ⚠️ A cache from before this field has none: every task in it is taken for a
        // cloud copy, so a load drops the ones the cloud no longer has.
        unsyncedIds: state.unsyncedIds,
        // ⚠️ A cache from before these has no marks: no unsaved edit, so its first load takes the cloud's copies.
        dirtyIds: state.dirtyIds,
        cloudVersions: state.cloudVersions,
        // ⚠️ Each unsaved row's base: without it a load drops that edit (lib/ownRowSync.ts). A cache from
        // before it has none.
        dirtyBases: state.dirtyBases,
      }),
      // The saved copy read back (a page load, a change of account): the base of each task's next
      // edit — as this device last saw it in the cloud; an unsaved one's, its saved base (lib/ownRowSync.ts
      // `restore`). ⚠️ Runs inside `create`, before `useTaskStore` exists: the marks come with `state`.
      onRehydrateStorage: () => (state) => { if (state) taskSync.restore(allTasks(state.tasks), state); },
      migrate: (persistedState: any, version: number) => {
        if (version < 8) {
          return {
            tasks: {},
            userLevel: 1
          } as unknown as TaskState;
        }
        // v9: migrate legacy subTasks into the task tree on rehydrate (offline).
        // The cloud write-through happens later in initialize() once authed.
        const state = persistedState as TaskState;
        if (state?.tasks) {
          const { tasks, created } = migrateSubtasksToTree(state.tasks);
          // Its new child tasks exist only here: unsynced, so a load keeps them and their
          // next write inserts them.
          const unsyncedIds = [...(state.unsyncedIds ?? []), ...created.map((t) => t.id)];
          return { ...state, tasks, unsyncedIds } as TaskState;
        }
        return state;
      }
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: the next account saw the last
   one's tasks until its own load; tasks that lived only here (made offline, or whose write never
   landed) were replaced by the next account's load; and the next account's tasks made before its
   own load were kept by the last one's next load — and inserted as its own on their next save. */
scopeToAccount(useTaskStore);

// ⭐ A tab that comes back shows the tasks the cloud holds now — read only (lib/ownRowSync.ts `rowFocusRefresh`):
// the load's own rows (open, or saved in the last 30 days), and every id, so an old completed task the filter
// leaves out isn't taken for a ghost. ⚠️ No subtask migration here: it writes (the next load runs it).
refreshBlobOnFocus(TASKS_TABLE, rowFocusRefresh(taskSync, {
  label: 'tasks',
  loadedFor: () => useTaskStore.getState().initializedUserId,
  held: () => allTasks(useTaskStore.getState().tasks),
  versions: () => supabase.from(TASKS_TABLE).select('id, updated_at').or(`completed.eq.false,updated_at.gte.${loadCutoff()}`),
  inCloud: readAllTaskIds,
  apply: (read) => useTaskStore.setState((state) => {
    const cached = allTasks(state.tasks);
    const merged = mergeLoad({
      cached, unsyncedIds: state.unsyncedIds, marks: marksOf(state), ...read.over(cached),
      keep: (t) => read.has(t.id), label: 'tasks',
    });
    const byCategory: Record<string, Task[]> = {};
    merged.rows.forEach((task) => { (byCategory[task.category ?? ''] ||= []).push(task); });
    return { tasks: byCategory, unsyncedIds: merged.unsyncedIds, ...merged.marks };
  }),
}));
